import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {FormattedMessage} from 'react-intl';

import BalancedFormattedMessage from '../../containers/balanced-formatted-message.jsx';
import Box from '../box/box.jsx';
import Dots from './dots.jsx';
import PeripheralTile from './peripheral-tile.jsx';
import {trConnectionModal} from '../debug-modal/sections/tr-messages';

import styles from './connection-modal.css';

// Türkçe içeriği kullanmak için TRUE değerini ayarlayın
const useTurkish = true;

const ScanningStep = props => {
    const [isScanning, setIsScanning] = useState(false);
    const [foundDevices, setFoundDevices] = useState([]);
    const [error, setError] = useState(null);
    const [scanStatus, setScanStatus] = useState('ready'); // ready, scanning, found, notfound

    const {
        connectionTipIconURL,
        extensionId,
        isRefreshing,
        onRefresh,
        onConnecting,
        onCancel,
        peripheralList,
        phase,
        scanning,
        showUpdate,
        vm
    } = props;

    const startBluetoothScan = async () => {
        try {
            setIsScanning(true);
            setScanStatus('scanning');
            setError(null);
            setFoundDevices([]);

            // Web Bluetooth API desteği kontrolü
            if (!navigator.bluetooth) {
                let errorMessage = 'Web Bluetooth API desteklenmiyor. ';
                
                // Tarayıcı kontrolü
                const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
                const isEdge = /Edg/.test(navigator.userAgent);
                
                if (isChrome) {
                    errorMessage += 'Chrome\'da chrome://flags/#enable-experimental-web-platform-features adresine gidip "Experimental Web Platform features" seçeneğini etkinleştirin.';
                } else if (isEdge) {
                    errorMessage += 'Edge\'de edge://flags/#enable-experimental-web-platform-features adresine gidip "Experimental Web Platform features" seçeneğini etkinleştirin.';
                } else {
                    errorMessage += 'Chrome veya Edge tarayıcısı kullanın.';
                }
                
                throw new Error(errorMessage);
            }

            console.log('Web Bluetooth API destekleniyor, cihaz taraması başlatılıyor...');

            // Bluetooth cihaz taraması başlat
            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: ['battery_service', 'device_information']
            });

            console.log('Bluetooth cihazı seçildi:', device);

            if (device) {
                setFoundDevices([{
                    id: device.id,
                    name: device.name || 'Bilinmeyen Cihaz',
                    device: device
                }]);
                setScanStatus('found');
            } else {
                setScanStatus('notfound');
            }

        } catch (err) {
            console.error('Bluetooth tarama hatası:', err);
            setIsScanning(false);
            setScanStatus('notfound');
            
            if (err.name === 'NotFoundError') {
                setError('Hiçbir Bluetooth cihazı seçilmedi. Lütfen bir cihaz seçin.');
            } else if (err.name === 'SecurityError') {
                setError('Bluetooth erişimi reddedildi. Tarayıcı izinlerini kontrol edin ve HTTPS kullandığınızdan emin olun.');
            } else if (err.name === 'NotSupportedError') {
                setError('Bu cihaz Bluetooth desteklemiyor veya Bluetooth kapalı.');
            } else if (err.message && err.message.includes('not supported')) {
                setError('Bu tarayıcı Bluetooth API\'yi desteklemiyor. Chrome veya Edge kullanın.');
            } else {
                setError(err.message || 'Bluetooth tarama hatası oluştu. Bluetooth\'un açık olduğundan emin olun.');
            }
        } finally {
            setIsScanning(false);
        }
    };

    const connectToDevice = async (device) => {
        try {
            setError(null);
            
            // Önce connecting durumunu bildir
            if (props.onConnecting) {
                props.onConnecting(device.id, device.name);
            }
            
            // Cihaza bağlanmayı dene
            await device.gatt.connect();
            
            // Başarılı bağlantıyı bildir
            if (props.onConnected) {
                props.onConnected();
            }
            
        } catch (err) {
            console.error('Cihaz bağlantı hatası:', err);
            setError(`${device.name} cihazına bağlanılamadı: ${err.message}`);
        }
    };

    const getStatusMessage = () => {
        switch (scanStatus) {
            case 'scanning':
                return 'Bluetooth cihazları aranıyor...';
            case 'found':
                return `${foundDevices.length} cihaz bulundu. Bağlanmak istediğiniz cihazı seçin.`;
            case 'notfound':
                return 'Hiçbir cihaz bulunamadı. Cihazınızın açık ve eşleştirme modunda olduğundan emin olun.';
            default:
                return 'Bluetooth cihazlarını aramak için "Tarama Başlat" butonuna tıklayın.';
        }
    };

    return (
        <Box className={styles.body}>
        <Box className={styles.activityArea}>
                    <div className={styles.activityAreaInfo}>
                        <div className={styles.centeredRow}>
                        {scanStatus === 'scanning' ? (
                            <React.Fragment>
                                <div style={{ 
                                    fontSize: '48px', 
                                    color: '#4D97FF',
                                    marginBottom: '10px',
                                    animation: 'spin 2s linear infinite'
                                }}>
                                    🔄
                                </div>
                                <div style={{ marginTop: '20px', color: '#4D97FF', fontWeight: 'bold' }}>
                                    🔍 Bluetooth cihazları aranıyor...
                                </div>
                            </React.Fragment>
                        ) : scanStatus === 'found' ? (
                            <React.Fragment>
                                <div style={{ 
                                    fontSize: '48px', 
                                    color: '#4CAF50',
                                    marginBottom: '10px'
                                }}>
                                    ✅
                                </div>
                                <div style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                                    Cihazlar bulundu!
                                </div>
                            </React.Fragment>
                        ) : scanStatus === 'notfound' ? (
                            <React.Fragment>
                                <div style={{ 
                                    fontSize: '48px', 
                                    color: '#FF6B35',
                                    marginBottom: '10px'
                                }}>
                                    ⚠️
                        </div>
                                <div style={{ color: '#FF6B35', fontWeight: 'bold', marginTop: '10px' }}>
                                    Cihaz bulunamadı
                    </div>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <div style={{ 
                                    fontSize: '48px', 
                                    color: '#4D97FF',
                                    marginBottom: '10px'
                                }}>
                                    📶
                                </div>
                                <div style={{ color: '#575E75', fontWeight: 'bold' }}>
                                    Bluetooth Cihaz Tarama
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>

                <div style={{
                    padding: '15px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '8px',
                    margin: '20px 0',
                    textAlign: 'center',
                    color: '#575E75'
                }}>
                    {getStatusMessage()}
                </div>

                {error && (
                    <div style={{
                        padding: '10px',
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        border: '1px solid #ffcdd2',
                        textAlign: 'center'
                    }}>
                        ⚠️ {error}
                    </div>
                )}

                {foundDevices.length > 0 && (
                    <Box className={styles.scrollableArea}>
                        {foundDevices.map((device, index) => (
                            <div
                                key={device.id || `device-${index}`}
                                className={styles.deviceTypeItem}
                                onClick={() => connectToDevice(device.device)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '15px',
                                    padding: '15px',
                                    marginBottom: '10px',
                                    cursor: 'pointer'
                                }}
                            >
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#4D97FF',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '20px'
                                }}>
                                    📱
                                </div>
                                <div>
                                    <div style={{ fontWeight: 'bold', color: '#575E75' }}>
                                        {device.name}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#8A8A8A' }}>
                                        ID: {device.id}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </Box>
                )}
            </Box>

            <Box className={styles.bottomArea}>
            <Box className={classNames(styles.bottomAreaItem, styles.buttonRow)}>
                    {scanStatus === 'ready' || scanStatus === 'notfound' ? (
                        <button
                            className={styles.connectionButton}
                            onClick={startBluetoothScan}
                            disabled={isScanning}
                            style={{
                                backgroundColor: isScanning ? '#ccc' : '#4D97FF',
                                color: 'white',
                                padding: '10px 20px',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: isScanning ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isScanning ? '🔄 Aranıyor...' : '🔍 Tarama Başlat'}
                        </button>
                    ) : (
                <button
                    className={styles.connectionButton}
                            onClick={() => {
                                setScanStatus('ready');
                                setFoundDevices([]);
                                setError(null);
                            }}
                            style={{
                                backgroundColor: '#FF6B35',
                                color: 'white',
                                padding: '10px 20px',
                                borderRadius: '4px',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            🔄 Yeniden Tara
                </button>
                    )}
                    
                    <button
                        className={styles.connectionButton}
                        onClick={onCancel}
                        style={{
                            backgroundColor: '#757575',
                            color: 'white',
                            padding: '10px 20px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            marginLeft: '10px'
                        }}
                    >
                        İptal
                    </button>
                </Box>
            </Box>
        </Box>
    );
};

ScanningStep.propTypes = {
    connectionSmallIconURL: PropTypes.string,
    connectionTipIconURL: PropTypes.string,
    extensionId: PropTypes.string,
    isRefreshing: PropTypes.bool,
    onConnecting: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onUpdatePeripheral: PropTypes.func,
    peripheralList: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        peripheralId: PropTypes.string,
        rssi: PropTypes.number
    })),
    phase: PropTypes.oneOf(['prescan', 'scan', 'connect', 'disconnect']),
    scanning: PropTypes.bool.isRequired,
    showUpdate: PropTypes.bool,
    vm: PropTypes.shape({
        connectPeripheral: PropTypes.func,
        disconnectPeripheral: PropTypes.func,
        getPeripheralIsConnected: PropTypes.func,
        scanForPeripheral: PropTypes.func
    }).isRequired
};

ScanningStep.defaultProps = {
    peripheralList: [],
    phase: 'scan',
    showUpdate: false
};

export default ScanningStep;
